<template>
  <v-row justify="center">
    <v-dialog
      origin="top center"
      v-model="dialogStatus"
      persistent
      content-class="warranty-dialog"
      :max-width="dialogWidth"
    >
      <v-card>
        <v-card-title
          class="headline dialog-custom-header white-background custom-border-bottom custom-header-blue-text"
        >
          <template v-if="lodash.isEmpty(warrantyDetail) === false">
            Renew Warranty -
            <span class="orange--text">{{ warrantyOption.unique_id }}</span>
          </template>
          <template v-else>
            Renew Warranty -
            <span class="orange--text">{{
              warrantyOption.unique_id
            }}</span></template
          >
        </v-card-title>
        <v-card-text class="p-8 font-size-16">
          <perfect-scrollbar
            :options="{ suppressScrollX: true }"
            class="scroll"
            style="height: calc(100vh - 280px); position: relative"
          >
            <v-form
              ref="warrantyForm"
              v-model.trim="formValid"
              lazy-validation
              v-on:submit.stop.prevent="update_or_create"
            >
              <v-container fluid class="py-0">
                <!-- Section 1: Target Information & Equipment Selection -->
                <div class="erp-form-section mb-6">
                  <div class="section-title d-flex align-center mb-3">
                    <v-icon small color="cyan darken-2" class="mr-2"
                      >mdi-cube-outline</v-icon
                    >
                    <span
                      class="text-subtitle-1 font-weight-bold text-uppercase custom-header-blue-text"
                    >
                      Target & Equipment Details
                    </span>
                  </div>

                  <v-row>
                    <v-col
                      md="6"
                      cols="12"
                      class="py-2"
                      v-if="warrantyOption?.customer > 0"
                    >
                      <label class="font-size-16 font-weight-500 ml-1">
                        Customer
                      </label>
                      <v-autocomplete
                        v-model.trim="warrantyOption.customer"
                        :items="allCustomerList"
                        clearable
                        dense
                        flat
                        filled
                        placeholder="Display Name"
                        item-color="cyan"
                        color="cyan"
                        solo
                        disabled
                        :loading="pageLoading"
                        item-text="display_name"
                        item-value="id"
                        hide-details="auto"
                      >
                        <template v-slot:no-data>
                          <v-list-item>
                            <v-list-item-title
                              v-html="'No Customer(s) Found.'"
                            ></v-list-item-title>
                          </v-list-item>
                        </template>
                        <template v-slot:item="{ item }">
                          <v-list-item-avatar>
                            <v-img
                              :lazy-src="$defaultProfileImage"
                              :src="$assetAPIURL(item.profile_logo)"
                              aspect-ratio="1"
                              class="margin-auto grey lighten-5 custom-grey-border"
                              transition="fade-transition"
                            ></v-img>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title
                              class="text-capitalize font-weight-500 font-size-16"
                            >
                              {{ item.display_name }}
                              <v-chip
                                color="blue white--text"
                                label
                                small
                                class="ml-2"
                                v-if="
                                  item.company_type &&
                                  item.company_type == 'company'
                                "
                              >
                                <span class="font-size-14 font-weight-500"
                                  >Company</span
                                >
                              </v-chip>
                              <v-chip
                                color="red white--text"
                                label
                                small
                                class="ml-2"
                                v-if="
                                  item.company_type &&
                                  item.company_type == 'personal'
                                "
                              >
                                <span class="font-size-14 font-weight-500"
                                  >Individual</span
                                >
                              </v-chip>
                            </v-list-item-title>
                            <v-list-item-title
                              class="text-capitalize font-weight-500 font-size-14 text--secondary"
                            >
                              {{ item.company_name }}
                            </v-list-item-title>
                          </v-list-item-content>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col
                      md="6"
                      cols="12"
                      class="py-2"
                      v-if="warrantyOption?.customer > 0"
                    >
                      <label class="font-size-16 font-weight-500 ml-1">
                        Site Location {{ warrantyOption.warranty_type }}
                      </label>
                      <v-autocomplete
                        hide-details="auto"
                        clearable
                        id="property-equipment"
                        :items="allSiteLocationList"
                        v-on:change="getOptions($event, 'location')"
                        v-model.trim="warrantyOption.address"
                        disabled
                        dense
                        flat
                        filled
                        placeholder="Site Location"
                        item-color="cyan"
                        color="cyan"
                        solo
                        :loading="pageLoading"
                        item-text="barcode"
                        item-value="id"
                      >
                        <template v-slot:no-data>
                          <v-list-item>
                            <v-list-item-title
                              v-html="'No Site Location(s) Found.'"
                            ></v-list-item-title>
                          </v-list-item>
                        </template>
                        <template v-slot:item="{ item }">
                          <v-list-item-icon class="mr-3">
                            <v-icon large>mdi-home-map-marker</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-layout style="max-width: 150px">
                              <v-flex>
                                <Barcode
                                  class="my-0 mt-0 mb-0 mr-1"
                                  :barcode="item.barcode"
                                ></Barcode>
                              </v-flex>
                              <v-flex v-if="item.is_tenant">
                                <v-chip
                                  class="mx-1"
                                  label
                                  small
                                  color="green"
                                  outlined
                                  >Tenant</v-chip
                                >
                              </v-flex>
                              <v-flex>
                                <v-chip
                                  label
                                  small
                                  class="ml-1"
                                  :color="item.type == 1 ? 'cyan' : 'orange'"
                                  outlined
                                >
                                  <template v-if="item.type == 1"
                                    >Site Location</template
                                  >
                                  <template v-else>Billing</template>
                                </v-chip>
                              </v-flex>
                            </v-layout>
                            <v-list-item-title
                              class="text-capitalize font-weight-500 font-size-16"
                            >
                              {{ get_formatted_property(item) }}
                            </v-list-item-title>
                          </v-list-item-content>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col md="12" cols="12" class="py-2">
                      <label class="font-size-16 font-weight-500 ml-1 required"
                        >Equipment</label
                      >
                      <v-autocomplete
                        v-model="warrantyOption.equipemt"
                        :items="allEquipemtList"
                        clearable
                        dense
                        flat
                        filled
                        placeholder="Equipment"
                        item-color="cyan"
                        color="cyan"
                        solo
                        disabled
                        :loading="pageLoading"
                        item-text="name"
                        item-value="id"
                        hide-details="auto"
                        :rules="[
                          validateRules.required(
                            warrantyOption.equipemt,
                            'Equipment'
                          ),
                        ]"
                      >
                        <template v-slot:no-data>
                          <v-list-item>
                            <v-list-item-title
                              v-html="'No Equipment(s) Found.'"
                            ></v-list-item-title>
                          </v-list-item>
                        </template>
                        <template v-slot:item="{ item }">
                          <v-list-item-content>
                            <v-list-item-title
                              class="text-capitalize font-weight-500 font-size-16 max-content-width"
                            >
                              {{ item.name }}
                            </v-list-item-title>
                            <v-list-item-title
                              class="text-capitalize font-weight-500 font-size-16 max-content-width"
                            >
                              {{ item.serial_no }}
                            </v-list-item-title>
                          </v-list-item-content>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <!-- Selected Equipment Summary Display Card -->
                    <v-col
                      md="12"
                      cols="12"
                      class="py-2"
                      v-if="warrantyOption.equipemt"
                    >
                      <v-card
                        outlined
                        class="grey lighten-5 rounded-lg px-4 py-2 border-dashed"
                      >
                        <v-row align="center">
                          <!-- Image Column -->
                          <v-col
                            cols="12"
                            sm="2"
                            class="text-center"
                            v-if="equipmentInfo"
                          >
                            <v-avatar size="72" class="elevation-1 white">
                              <v-img
                                v-if="equipmentInfo.image"
                                :src="equipmentInfo.image"
                                contain
                              ></v-img>
                              <v-img
                                v-else
                                :src="$defaultImage"
                                contain
                              ></v-img>
                            </v-avatar>
                          </v-col>

                          <!-- Primary Details Column -->
                          <v-col cols="12" sm="5" v-if="equipmentInfo">
                            <div class="info-group">
                              <div
                                class="d-flex align-center mb-1"
                                v-if="equipmentInfo.barcode"
                              >
                                <span
                                  class="font-weight-600 text-caption-1 text--secondary mr-2"
                                  >ID / Barcode:</span
                                >
                                <v-chip
                                  color="cyan white--text"
                                  label
                                  x-small
                                  class="font-weight-bold"
                                >
                                  {{ equipmentInfo.barcode }}
                                </v-chip>
                              </div>

                              <div
                                class="d-flex align-center mb-1"
                                v-if="equipmentInfo.serial_no"
                              >
                                <span
                                  class="font-weight-600 text-caption-1 text--secondary mr-2"
                                  >Serial No:</span
                                >
                                <span
                                  class="text-body-1 font-weight-medium text--primary"
                                >
                                  {{ equipmentInfo.serial_no }}
                                </span>
                              </div>

                              <div
                                class="d-flex align-center mb-1"
                                v-if="equipmentInfo.name"
                              >
                                <span
                                  class="font-weight-600 text-caption-1 text--secondary mr-2"
                                  >Name:</span
                                >
                                <span
                                  class="text-body-1 font-weight-bold text--primary"
                                >
                                  {{ equipmentInfo.name }}
                                </span>
                              </div>
                            </div>
                          </v-col>

                          <!-- Secondary Details Column -->
                          <v-col cols="12" sm="5" v-if="equipmentInfo">
                            <div class="info-group">
                              <div class="d-flex align-center mb-1">
                                <span
                                  class="font-weight-600 text-caption-1 text--secondary mr-2"
                                  >Brand:</span
                                >
                                <span class="text-body-1 text--primary">
                                  {{ equipmentInfo.brand || "No brand" }}
                                </span>
                              </div>

                              <div
                                class="d-flex align-center mb-1"
                                v-if="equipmentInfo.model"
                              >
                                <span
                                  class="font-weight-600 text-caption-1 text--secondary mr-2"
                                  >Model:</span
                                >
                                <span class="text-body-1 text--primary">
                                  {{ equipmentInfo.model }}
                                </span>
                              </div>

                              <div
                                class="d-flex align-center mb-1"
                                v-if="equipmentInfo.do_no"
                              >
                                <span
                                  class="font-weight-600 text-caption-1 text--secondary mr-2"
                                  >DO No:</span
                                >
                                <span class="text-body-1 text--primary">
                                  {{ equipmentInfo.do_no }}
                                </span>
                              </div>
                            </div>
                          </v-col>
                        </v-row>

                        <!-- Contact & Address Sub-Section -->
                        <v-row
                          class="mt-2 pt-2 border-top"
                          v-if="
                            equipmemntArr &&
                            (equipmemntArr.contact_person ||
                              equipmemntArr.property_address)
                          "
                        >
                          <v-col
                            cols="12"
                            sm="6"
                            v-if="equipmemntArr.contact_person"
                          >
                            <div
                              class="text-caption-1 font-weight-bold text-uppercase text--secondary mb-1"
                            >
                              Contact Details
                            </div>
                            <div
                              class="d-flex align-center text-body-1 py-1"
                              v-if="equipmemntArr.contact_person.display_name"
                            >
                              <v-icon small color="cyan darken-2" class="mr-2"
                                >mdi-account</v-icon
                              >
                              <span>{{
                                equipmemntArr.contact_person.display_name
                              }}</span>
                            </div>
                            <div
                              class="d-flex align-center text-body-1 py-1"
                              v-if="equipmemntArr.contact_person.primary_phone"
                            >
                              <v-icon small color="green darken-1" class="mr-2"
                                >mdi-phone</v-icon
                              >
                              <span>{{
                                equipmemntArr.contact_person.primary_phone
                              }}</span>
                            </div>
                            <div
                              class="d-flex align-center text-body-1 py-1"
                              v-if="equipmemntArr.contact_person.primary_email"
                            >
                              <v-icon small color="blue darken-1" class="mr-2"
                                >mdi-email</v-icon
                              >
                              <span>{{
                                equipmemntArr.contact_person.primary_email
                              }}</span>
                            </div>
                          </v-col>

                          <v-col
                            cols="12"
                            sm="6"
                            v-if="equipmemntArr.property_address"
                          >
                            <div
                              class="text-caption-1 font-weight-bold text-uppercase text--secondary mb-1"
                            >
                              Company Address
                            </div>
                            <div class="d-flex align-start text-body-1 py-1">
                              <v-icon
                                small
                                color="red darken-1"
                                class="mr-2 mt-1"
                                >mdi-map-marker</v-icon
                              >
                              <div>
                                <span
                                  v-if="equipmemntArr?.property_name"
                                  class="font-weight-bold d-block"
                                >
                                  {{ equipmemntArr?.property_name }}
                                </span>
                                <span>{{ equipmemntArr.street_1 }}</span>
                                <span v-if="equipmemntArr.street_2"
                                  >, {{ equipmemntArr.street_2 }}</span
                                >
                                <span v-if="equipmemntArr.unit_no"
                                  >, {{ equipmemntArr.unit_no }}</span
                                >
                                <br />
                                <span
                                  >{{ equipmemntArr.country }},
                                  {{ equipmemntArr.zip_code }}</span
                                >
                              </div>
                            </div>
                          </v-col>
                        </v-row>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>

                <!-- Section 2: Warranty Renewal Terms & Financials -->
                <div class="erp-form-section mb-6">
                  <div class="section-title d-flex align-center mb-3">
                    <v-icon small color="cyan darken-2" class="mr-2"
                      >mdi-calendar-clock</v-icon
                    >
                    <span
                      class="text-subtitle-1 font-weight-bold text-uppercase custom-header-blue-text"
                    >
                      Renewal Details
                    </span>
                  </div>

                  <v-row>
                    <!-- Duration -->
                    <v-col md="6" cols="12" class="py-2">
                      <label class="font-size-16 font-weight-500 ml-1 required">
                        Duration
                      </label>
                      <v-text-field
                        dense
                        v-mask="'###'"
                        v-model.trim="warrantyOption.duration"
                        filled
                        placeholder="e.g. 12"
                        solo
                        flat
                        color="cyan"
                        class="width-100"
                        hide-details="auto"
                        :rules="[
                          validateRules.required(
                            warrantyOption.duration,
                            'Duration'
                          ),
                        ]"
                        v-on:keyup="
                          (e) => updateWarrantyDuration(e, 'warranty_duration')
                        "
                      ></v-text-field>
                    </v-col>

                    <!-- Duration Type -->
                    <v-col md="6" cols="12" class="py-2">
                      <label class="font-size-16 font-weight-500 ml-1 required">
                        Duration Type
                      </label>
                      <v-select
                        :items="warrantyDurationTypes"
                        v-model.trim="warrantyOption.duration_type"
                        dense
                        filled
                        solo
                        flat
                        class="width-100"
                        placeholder="Select Duration Type"
                        color="cyan"
                        item-text="text"
                        item-value="value"
                        item-color="cyan"
                        hide-details="auto"
                        :rules="[
                          validateRules.required(
                            warrantyOption.duration_type,
                            'Duration Type'
                          ),
                        ]"
                        v-on:change="
                          (e) =>
                            updateWarrantyDuration(e, 'warranty_duration_type')
                        "
                      ></v-select>
                    </v-col>

                    <!-- Start Date -->
                    <v-col md="6" cols="12" class="py-2">
                      <label class="font-size-16 font-weight-500 ml-1">
                        Start Date
                      </label>
                      <DatePicker
                        solo
                        :defaultDate="warrantyOption.start_date"
                        :pageLoading="pageLoading"
                        :placeholder="'Start Date'"
                        :minDate="warrantyOption.start_date"
                        v-model="warrantyOption.start_date"
                        v-on:change="
                          (e) => updateWarrantyDuration(e, 'start_date')
                        "
                      ></DatePicker>
                    </v-col>

                    <!-- Calculated End Date -->
                    <v-col md="6" cols="12" class="py-2">
                      <label class="font-size-16 font-weight-500 ml-1">
                        Calculated End Date
                      </label>
                      <div
                        class="pa-2 rounded border grey lighten-5 d-flex align-center"
                      >
                        <v-icon small color="cyan darken-2" class="mr-2"
                          >mdi-calendar-check</v-icon
                        >
                        <span
                          class="font-weight-bold text-body-1 color-custom-blue"
                        >
                          {{ formatDate(warrantyOption.end_date) || "N/A" }}
                        </span>
                      </div>
                    </v-col>

                    <!-- Reference No -->
                    <v-col md="6" cols="12" class="py-2">
                      <label class="font-size-16 font-weight-500 ml-1 required">
                        Reference No.
                      </label>
                      <v-text-field
                        dense
                        filled
                        solo
                        flat
                        :rules="[
                          validateRules.required(
                            warrantyOption.reference,
                            'Reference No.'
                          ),
                        ]"
                        color="cyan"
                        placeholder="Enter Reference No."
                        v-model.trim="warrantyOption.reference"
                        hide-details="auto"
                      ></v-text-field>
                    </v-col>

                    <!-- Cost -->
                    <v-col md="6" cols="12" class="py-2">
                      <label class="font-size-16 font-weight-500 ml-1">
                        Cost
                      </label>
                      <PriceField
                        dense
                        filled
                        color="cyan"
                        solo
                        flat
                        placeholder="Cost"
                        v-model.trim="warrantyOption.cost"
                        hide-details="auto"
                      ></PriceField>
                    </v-col>

                    <!-- Description -->
                    <v-col md="12" cols="12" class="py-2">
                      <label class="font-size-16 font-weight-500 ml-1">
                        Description
                      </label>
                      <v-textarea
                        v-model.trim="warrantyOption.description"
                        auto-grow
                        dense
                        filled
                        solo
                        flat
                        color="cyan"
                        placeholder="Description..."
                        row-height="20"
                        hide-details="auto"
                        v-on:keypress="(e) => manageLimit(e)"
                        v-on:paste="(e) => onPaste(e)"
                      ></v-textarea>
                      <div class="text-right text-caption text--secondary mt-1">
                        {{
                          warrantyOption.description
                            ? warrantyOption.description.length
                            : 0
                        }}/200
                      </div>
                    </v-col>

                    <!-- Attachments -->
                    <v-col md="12" cols="12" class="py-2">
                      <table
                        width="100%"
                        style="table-layout: fixed"
                        class="my-auto py-0 align-items-center"
                      >
                        <tr
                          v-for="(row, index) in files"
                          :key="`create-visit-attachment-${index}`"
                        >
                          <td class="">
                            <v-file-input
                              hide-details
                              class="px-0"
                              dense
                              filled
                              solo
                              flat
                              v-model="row.file"
                              placeholder="Select File"
                              prepend-icon=""
                              prepend-inner-icon="$file"
                              v-on:change="updateFile(index, $event)"
                              v-on:click:clear="updateFile(index, $event)"
                              color="cyan"
                            />
                          </td>
                          <td class="py-0">
                            <v-text-field
                              hide-details
                              class="px-0"
                              v-model="row.name"
                              placeholder="Name"
                              dense
                              filled
                              solo
                              flat
                              color="cyan"
                            />
                          </td>
                          <td class="px-0 py-0" width="5%" align="center">
                            <v-btn
                              :disabled="files.length < 2"
                              v-on:click="removeFile(index)"
                              icon
                              class="w-100"
                              color="red lighten-1"
                            >
                              <v-icon>mdi-delete</v-icon>
                            </v-btn>
                          </td>
                        </tr>
                        <tr>
                          <td class="" colspan="3">
                            <v-btn
                              color="cyan white--text"
                              class="custom-bold-button"
                              depressed
                              v-on:click="addMore()"
                              tile
                              >Add More</v-btn
                            >
                          </td>
                        </tr>
                      </table>
                    </v-col>
                  </v-row>
                </div>
              </v-container>
            </v-form>
          </perfect-scrollbar>
        </v-card-text>
        <v-divider class="my-0"></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <div class="warrant-manage-action">
            <v-btn
              depressed
              :disabled="!formValid || pageLoading"
              :loading="pageLoading"
              v-on:click="update_or_create"
              class="mx-2 custom-bold-button white--text"
              color="cyan"
              >Save
            </v-btn>
            <v-btn
              depressed
              :loading="pageLoading"
              :disabled="pageLoading"
              v-on:click="$emit('close:dialog')"
              class="mx-2 custom-grey-border custom-bold-button"
              >Cancel
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<style scoped>
.v-dialog__content {
  align-items: start;
}
.v-autocomplete:not(.v-input--is-focused).v-select--chips input {
  max-height: 18px;
  padding: 0;
}
.v-text-field--filled > .v-input__control > .v-input__slot {
  min-height: 100%;
}
.border-dashed {
  border: 1px dashed #cbd5e1 !important;
}
.border-top {
  border-top: 1px solid #e2e8f0 !important;
}
.color-custom-blue {
  color: #1e293b;
}
</style>

<script>
import DatePicker from "@/view/pages/partials/Datepicker.vue";
import {
  /* , PUT, CLEAR_ERROR , */ GET,
  POST,
  CLEAR_ERROR,
  QUERY,
} from "@/core/services/store/request.module";
import ValidationMixin from "@/core/plugins/validation-mixin";
import moment from "moment";
import { toSafeInteger } from "lodash";
import Barcode from "@/view/pages/partials/Barcode.vue";
import CommonMixin from "@/core/plugins/common-mixin";

export default {
  name: "create-or-update-warranty",
  mixins: [ValidationMixin, CommonMixin],
  props: {
    dialogStatus: {
      type: Boolean,
      default: false,
    },
    /*  allCustomerList: {
        type: Array,
        default: () => {
          return new Array();
        },
      },
      allProductList: {
        type: Array,
        default: () => {
          return new Array();
        },
      }, */
    warrantyDetail: {
      type: Object,
      default: () => {
        return new Object();
      },
    },
  },
  components: {
    DatePicker,
    Barcode,
  },
  watch: {
    warrantyDetail: {
      deep: true,
      immediate: true,
      handler() {
        if (this.lodash.isEmpty(this.warrantyDetail) === false) {
          this.warrantyOption.id = this.warrantyDetail.id;
          this.warrantyOption.customer = this.warrantyDetail.type_id;
          this.warrantyOption.equipemt = this.warrantyDetail.service_id;
          this.warrantyOption.cost = this.warrantyDetail.cost;
          this.warrantyOption.warranty_type = this.warrantyDetail.type;
          this.warrantyOption.start_date = moment(
            this.warrantyDetail.warranty_end_date_formatted,
            "YYYY-MM-DD"
          )
            .add(1, "days")
            .format("YYYY-MM-DD");
          this.warrantyOption.end_date =
            this.warrantyDetail.warranty_end_date_formatted;
          this.warrantyOption.duration = toSafeInteger(
            this.warrantyDetail.duration
          );
          this.warrantyOption.duration_type = this.warrantyDetail.duration_type;
          this.warrantyOption.description = this.warrantyDetail.description;
          this.warrantyOption.equipemt_type = this.warrantyDetail.equipemt_type;
          this.warrantyOption.unique_id = this.barcode;
          this.warrantyOption.address = this.warrantyDetail.property
            ? this.warrantyDetail.property
            : 0;
          //console.log(this.warrantyOption.address);
          this.customerId = this.warrantyDetail.type_id;
        }
      },
    },
    dialogStatus: {
      deep: true,
      immediate: true,
      handler(param) {
        if (param) {
          this.getOptions();
          this.getAllCustomers();
          this.getCustomerEquipment();
          this.getEquipment(this.warrantyOption.equipemt);
          this.updateWarrantyDuration(this.warrantyOption.start_date);
        }
      },
    },
  },
  data() {
    return {
      customerId: 0,
      locationId: 0,
      allSiteLocationList: [],
      formValid: true,
      pageLoading: false,
      equipmemntArr: {},
      startDatePicker: null,
      defaultItemWarranty: new Array(),
      allEquipemtList: [],
      allCustomerList: [],
      barcode: null,
      equipmentInfo: {},
      files: [
        {
          file: null,
          name: null,
        },
      ],
      warrantyOption: new Object({
        id: null,
        customer: 0,
        equipemt: 0,
        cost: 0,
        warranty_type: "customer",
        reference: null,
        start_date: null,
        duration: 0,
        duration_type: "day",
        end_date: null,
        unique_id: null,
        description: null,
      }),
      warrantyList: [
        { text: "30 Days", value: "30", type: "day" },
        { text: "6 Months", value: "182", type: "day" },
        { text: "1 Year", value: "365", type: "day" },
        { text: "2 Years", value: "730", type: "day" },
        { text: "3 Years", value: "1095", type: "day" },
      ],
      warrantyDurationTypes: [
        {
          text: "Day(s)",
          value: "day",
        },
        {
          text: "Month(s)",
          value: "month",
        },
        {
          text: "Year(s)",
          value: "year",
        },
      ],
    };
  },
  methods: {
    get_formatted_property(row) {
      let tempArr = [];
      if (row.unit_no) {
        tempArr.push(row.unit_no);
      }
      if (row.street_1) {
        tempArr.push(row.street_1);
      }
      if (row.street_2) {
        tempArr.push(row.street_2);
      }
      if (row.zip_code) {
        tempArr.push(row.zip_code);
      }
      if (row.country_name) {
        tempArr.push(row.country_name);
      }
      return tempArr.join(", ");
    },
    onPaste(e) {
      let coppied = e.clipboardData.getData("Text");
      let preVal = this.warrantyOption.description;
      let finalval = String(preVal) + String(coppied);
      if (Number(finalval.length) > 199) {
        let trimValue = finalval.substring(0, 200);
        this.warrantyOption.description = trimValue;
        e.preventDefault();
      }
    },

    manageLimit(e) {
      if (
        this.warrantyOption.description &&
        this.warrantyOption.description.length > 199
      ) {
        e.preventDefault();
      }
    },

    updateWarrantyDuration(e, type) {
      let warranty_start_date = e;
      if (type != "start_date") {
        warranty_start_date =
          this.warrantyOption && this.warrantyOption.start_date
            ? this.warrantyOption.start_date
            : null;
      }
      if (warranty_start_date) {
        let date = moment(warranty_start_date, "YYYY-MM-DD");

        let duration = this.warrantyOption.duration
          ? toSafeInteger(this.warrantyOption.duration)
          : 0;
        switch (this.warrantyOption.duration_type) {
          case "day":
            date = moment(warranty_start_date, "YYYY-MM-DD").add(duration, "d");
            break;
          case "month":
            date = moment(warranty_start_date, "YYYY-MM-DD").add(duration, "M");
            break;
          case "year":
            date = moment(warranty_start_date, "YYYY-MM-DD").add(duration, "y");
            break;
        }
        date.subtract(1, "days");
        this.warrantyOption.end_date = this.lodash.cloneDeep(
          date.format("YYYY-MM-DD")
        );
      }
    },

    update_or_create() {
      const _this = this;
      if (!_this.$refs.warrantyForm.validate()) {
        return false;
      }
      _this.$store.dispatch(CLEAR_ERROR, {});
      _this.pageLoading = true;
      let formData = new FormData();
      formData.append(
        "type",
        this.warrantyOption.warrantyOption
          ? this.warrantyOption.warrantyOption
          : "customer"
      );
      formData.append(
        "type_id",
        this.warrantyOption.customer ? this.warrantyOption.customer : 0
      );

      formData.append(
        "site_location",
        this.warrantyOption.address ? this.warrantyOption.address : 0
      );

      formData.append(
        "service_id",
        this.warrantyOption.equipemt ? this.warrantyOption.equipemt : 0
      );
      formData.append(
        "duration",
        this.warrantyOption.duration ? this.warrantyOption.duration : 0
      );
      formData.append(
        "duration_type",
        this.warrantyOption.duration_type
          ? this.warrantyOption.duration_type
          : "day"
      );
      if (this.warrantyOption && this.warrantyOption.reference) {
        formData.append(
          "reference",
          this.warrantyOption.reference ? this.warrantyOption.reference : null
        );
      }
      if (this.warrantyOption && this.warrantyOption.start_date) {
        formData.append(
          "start_date",
          this.warrantyOption.start_date ? this.warrantyOption.start_date : null
        );
      }
      if (this.warrantyOption && this.warrantyOption.end_date) {
        formData.append(
          "end_date",
          this.warrantyOption.end_date ? this.warrantyOption.end_date : null
        );
      }
      if (this.warrantyOption && this.warrantyOption.cost) {
        formData.append(
          "cost",
          this.warrantyOption.cost ? this.warrantyOption.cost : 0
        );
      }
      if (this.warrantyOption && this.warrantyOption.description) {
        formData.append(
          "remark",
          this.warrantyOption.description
            ? this.warrantyOption.description
            : null
        );
      }
      if (this.warrantyOption && this.warrantyOption.equipemt_type) {
        formData.append(
          "equipemt_type",
          this.warrantyOption.equipemt_type
            ? this.warrantyOption.equipemt_type
            : null
        );
      }
      if (this.warrantyOption && this.warrantyOption.unique_id) {
        formData.append(
          "unique_id",
          this.warrantyOption.unique_id ? this.warrantyOption.unique_id : null
        );
      }
      for (let i = 0; i < this.files.length; i++) {
        if (this.files && this.files[i] && this.files[i].file) {
          formData.append(`file[${i}][file]`, this.files[i].file);
          formData.append(`file[${i}][name]`, this.files[i].name);
        }
      }

      let requestTYPE = POST;
      let requestURL = `warranty/${this.warrantyOption.id}/renew`;
      this.$store
        .dispatch(requestTYPE, {
          url: requestURL,
          data: formData,
        })
        .then(() => {
          _this.$emit("update:warranty", true);
          _this.$emit("close:dialog");
        })
        .catch((error) => {
          console.log({ error });
        })
        .finally(() => {
          this.pageLoading = false;
        });
    },
    updateFile(index, file, value, data) {
      if (value == "keyword") {
        if (file && file.name) {
          data[index].name = file.name.split(".").slice(0, -1).join(".");
          data[index].suffix = `.${file.name.split(".").pop()}`;
        } else {
          data[index].name = null;
          data[index].suffix = null;
        }
      } else {
        if (file && file.name) {
          this.files[index].name = file.name.split(".").slice(0, -1).join(".");
          this.files[index].suffix = `.${file.name.split(".").pop()}`;
        } else {
          this.files[index].name = null;
          this.files[index].suffix = null;
        }
      }
    },
    getEquipment(id) {
      if (id && id > 0) {
        const _this = this;
        _this.$store
          .dispatch(QUERY, { url: `equipment/${id}` })
          .then(({ data }) => {
            this.equipmentInfo = data;
            this.equipmemntArr = data.property;
          })
          .catch((error) => {
            _this.logError(error);
          });
      }
    },
    addMore() {
      this.files.push({
        file: null,
        name: null,
      });
    },
    getAllCustomers() {
      const _this = this;
      _this.$store
        .dispatch(GET, { url: "customer-list" })
        .then(({ data }) => {
          _this.allCustomerList = data;
        })
        .catch((error) => {
          _this.logError(error);
        });
    },
    getCustomerEquipment() {
      const _this = this;
      _this.$store
        .dispatch(GET, { url: "equipment-list" })
        .then(({ data }) => {
          _this.allEquipemtList = data;
        })
        .catch((error) => {
          _this.logError(error);
        });
    },
    getOptions() {
      const _this = this;
      if (this.customerId) {
        _this.customerId = this.customerId;
      }
      _this.$store
        .dispatch(QUERY, {
          url: "warranty/options",
          data: { customer: this.customerId ? this.customerId : 0 },
        })
        .then(({ data }) => {
          _this.barcode = data;
          this.warrantyOption.unique_id = _this.barcode.barcode;
          _this.allSiteLocationList = data.address ? data.address : [];
        })
        .catch((error) => {
          _this.logError(error);
        });
    },

    removeFile(index) {
      this.files.splice(index, 1);
      if (this.files.length < 1) {
        this.addMore();
      }
    },
    removeWarranty(index) {
      this.defaultItemWarranty.splice(index, 1);
    },
  },
  computed: {
    dialogWidth() {
      return this.lodash.toSafeInteger((document.body.clientWidth / 100) * 60);
    },
  },
};
</script>
